<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">

    <div class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col">

      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white">
            {{ form.id ? 'Editar Unidad' : 'Nueva Unidad' }}
          </h2>
          <p class="text-xs text-slate-500">Ej: Caja, Unidad, Litro, Metro.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6 space-y-5">

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Nombre de la Unidad</label>
          <div class="relative">
            <Scale class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              v-model="form.name"
              type="text"
              required
              class="form-input-premium pl-10"
              placeholder="Ej: Caja de 12"
              autofocus
            />
          </div>
        </div>

        <div>
          <label class="text-xs font-semibold text-slate-500 uppercase mb-1 block">Abreviatura / Código</label>
          <div class="relative">
            <Hash class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              v-model="form.code"
              type="text"
              required
              class="form-input-premium pl-10 uppercase"
              placeholder="Ej: CJA, UND, KGM"
            />
          </div>
          <p class="text-[10px] text-slate-400 mt-1">
            * Importante para Facturación Electrónica (DTE).
          </p>
        </div>

        <div class="pt-4 flex justify-end gap-3 border-t border-slate-100 dark:border-slate-800">
          <button
            type="button"
            @click="$emit('close')"
            class="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-600 dark:hover:bg-slate-700 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            :disabled="isSaving"
            class="px-6 py-2 text-sm font-bold text-white bg-brand-600 rounded-xl hover:bg-brand-700 shadow-lg shadow-brand-500/30 transition-all flex items-center gap-2 disabled:opacity-70"
          >
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import { X, Save, Loader2, Scale, Hash } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: Boolean,
  unitToEdit: Object
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError, showWarning } = useToast();

const isSaving = ref(false);
const currentUser = SecureStorage.getItem('llevo_user');
const currentCompanyId = currentUser?.company;

const form = reactive({
  id: null,
  company: currentCompanyId,
  name: '',
  code: ''
});

watch(() => props.unitToEdit, (newVal) => {
  if (newVal) {
    Object.assign(form, {
      id: newVal.id,
      company: newVal.company || currentCompanyId,
      name: newVal.name,
      code: newVal.code
    });
  } else {
    Object.assign(form, {
      id: null,
      company: currentCompanyId,
      name: '',
      code: ''
    });
  }
});

const handleSubmit = async () => {
  if (!form.name.trim() || !form.code.trim()) {
    showWarning('Nombre y Código son obligatorios.');
    return;
  }

  isSaving.value = true;
  try {
    const payload = { ...form };

    if (form.id) {
      await apiClient.put(`/units/${form.id}`, payload);
      showSuccess('Unidad actualizada.');
    } else {
      await apiClient.post('/units', payload);
      showSuccess('Unidad creada.');
    }
    emit('saved');
    emit('close');
  } catch (error) {
    console.error(error);
    showError('Error al guardar la unidad.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
.animate-fade-in {
  animation: fade-in 0.2s ease-out forwards;
}
</style>